import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import ViewColumnRoundedIcon from "@mui/icons-material/ViewColumnRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";

import BadgeIconButton from "./BadgeIconButton";
import SettingsPopupMenu from "./SettingsPopupMenu";
import FilterSettingsPanel from "./FilterSettingsPanel";

import { useIntl } from "react-intl";

import columnConfig from "../helpers/columnConfig";
import { ROOT_GROUP_ID, TABLE_HEADERS } from "../constants";


export const PERIOD_PRESETS = {
  ALL: "all",
  LAST_7_DAYS: "last7",
  LAST_30_DAYS: "last30",
  CUSTOM: "custom",
};

const sectionTitleSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "text.disabled",
};

const getFilterCount = (filters) =>
  (filters?.productTypes?.length ?? 0) +
  (filters?.supplierOrderStatusIds?.length ?? 0) +
  (filters?.periodPreset !== "all" ? 1 : 0);

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const subtractDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() - days);
  return nextDate;
};

const isDateRangeInvalid = (filters = {}) => {

  return Boolean(
    filters.orderDateFrom &&
    filters.orderDateTo &&
    filters.orderDateFrom > filters.orderDateTo
  );
};


const getPlacementLabel = (
  intl,
  groups,
  groupTarget
) => {
  if (groupTarget === ROOT_GROUP_ID) {
    return intl.formatMessage({
      id: "incomingOrders.views.mainLevel",
    });
  }

  return (
    groups.find((group) => group.id === groupTarget)?.label ??
    intl.formatMessage({
      id: "incomingOrders.views.mainLevel",
    })
  );
};


const getPresetPeriodRange = (periodPreset) => {
  const today = new Date();

  if (periodPreset === PERIOD_PRESETS.LAST_7_DAYS) {
    return {
      orderDateFrom: toDateInputValue(subtractDays(today, 6)),
      orderDateTo: toDateInputValue(today),
    };
  }

  if (periodPreset === PERIOD_PRESETS.LAST_30_DAYS) {
    return {
      orderDateFrom: toDateInputValue(subtractDays(today, 29)),
      orderDateTo: toDateInputValue(today),
    };
  }

  return {
    orderDateFrom: "",
    orderDateTo: "",
  };
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    borderRadius: "8px",
    bgcolor: "background.paper",
  },
  "& .MuiInputBase-input": {
    fontSize: 13,
  },
};

const labeledTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    borderRadius: "8px",
    bgcolor: "background.paper",
  },
  "& .MuiInputBase-input": {
    fontSize: 13,
  },
  "& .MuiInputLabel-root": {
    bgcolor: "background.paper",
    px: 0.5,
  },
};

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 500,
      color: "text.secondary",
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

const ColumnSettingsPanel = ({
  anchorEl,
  open,
  onClose,
  visibleColumns,
  onToggleColumn,
}) => {
  const intl = useIntl();

  return (
    <SettingsPopupMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      title={intl.formatMessage({
        id: "incomingOrders.views.showColumns"
      })}
      subtitle={intl.formatMessage({
        id: "incomingOrders.views.selectColumnsHelp",
      })}
    >
      {TABLE_HEADERS.map((columnType) => {
        const { label, labelId, required } = columnConfig[columnType] ?? { label: columnType };
        const checked = visibleColumns[columnType] !== false;

        return (
          <MenuItem
            key={columnType}
            dense
            disabled={Boolean(required)}
            onClick={() => onToggleColumn(columnType)}
            sx={{ px: 1, py: 0.25 }}
          >
            <Checkbox size="small" checked={checked} />
            <ListItemText
              primary={labelId ? intl.formatMessage({ id: labelId }) : label}
              primaryTypographyProps={{ fontSize: 13 }}
              sx={{ mr: 1 }}
            />
          </MenuItem>
        );
      })}
    </SettingsPopupMenu>
  );
};

const SettingsIconRow = ({
  filters,
  filterPanelOpen,
  onOpenFilterPanel,
  columnPanelOpen,
  onOpenColumnPanel,
  visibleColumnCount,
}) => {
  const intl = useIntl();
  const filterCount = getFilterCount(filters);

  return (
    <Box>
      <Typography sx={{ ...sectionTitleSx, mb: 0.75 }}>{intl.formatMessage({ id: "incomingOrders.toolbar.settings" })}</Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <BadgeIconButton
          title={intl.formatMessage({ id: "incomingOrders.toolbar.filterByTypeAndStatus" })}
          onClick={onOpenFilterPanel}
          badgeContent={filterCount}
          active={filterPanelOpen}
        >
          <FilterListRoundedIcon sx={{ fontSize: 18 }} />
        </BadgeIconButton>

        <BadgeIconButton
          title={intl.formatMessage({ id: "incomingOrders.toolbar.showHideColumns" })}
          onClick={onOpenColumnPanel}
          badgeContent={visibleColumnCount}
          active={columnPanelOpen}
        >
          <ViewColumnRoundedIcon sx={{ fontSize: 18 }} />
        </BadgeIconButton>
      </Stack>
    </Box>
  );
};

const PeriodSelector = ({ filters, onChange }) => {
  const intl = useIntl();

  const periodMode = filters.periodPreset;
  const hasInvalidDateRange = isDateRangeInvalid(filters);

  const handleModeChange = (_, nextMode) => {
    if (!nextMode) return;

    if (nextMode === PERIOD_PRESETS.ALL) {
      onChange({
        ...filters,
        periodPreset: PERIOD_PRESETS.ALL,
        orderDateFrom: "",
        orderDateTo: "",
      });
      return;
    }

    if (
      nextMode === PERIOD_PRESETS.LAST_7_DAYS ||
      nextMode === PERIOD_PRESETS.LAST_30_DAYS
    ) {
      onChange({
        ...filters,
        periodPreset: nextMode,
        ...getPresetPeriodRange(nextMode),
      });
      return;
    }

    onChange({
      ...filters,
      periodPreset: PERIOD_PRESETS.CUSTOM,
    });
  };

  const handlePeriodPatch = (patch) => {
    onChange({
      ...filters,
      periodPreset: PERIOD_PRESETS.CUSTOM,
      ...patch,
    });
  };

  return (
    <Box>
      <Typography sx={{ ...sectionTitleSx, mb: 0.75 }}>{intl.formatMessage({ id: "incomingOrders.views.period" })}</Typography>
      <ToggleButtonGroup
        exclusive
        value={periodMode}
        size="small"
        onChange={handleModeChange}
        sx={{
          mb: periodMode === PERIOD_PRESETS.CUSTOM ? 1 : 0,
          flexWrap: "wrap",
          "& .MuiToggleButton-root": {
            height: 28,
            textTransform: "none",
            fontSize: 12,
            px: 1.25,
          },
        }}
      >
        <ToggleButton value={PERIOD_PRESETS.ALL}>{intl.formatMessage({ id: "incomingOrders.views.allDates" })}</ToggleButton>
        <ToggleButton value={PERIOD_PRESETS.LAST_7_DAYS}>{intl.formatMessage({ id: "incomingOrders.views.lastWeek" })}</ToggleButton>
        <ToggleButton value={PERIOD_PRESETS.LAST_30_DAYS}>{intl.formatMessage({ id: "incomingOrders.views.latestMonth" })}</ToggleButton>
        <ToggleButton value={PERIOD_PRESETS.CUSTOM}>{intl.formatMessage({ id: "incomingOrders.views.custom" })}</ToggleButton>
      </ToggleButtonGroup>

      {periodMode === PERIOD_PRESETS.CUSTOM && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Box sx={{ flex: 1 }}>
            <FieldLabel>{intl.formatMessage({ id: "incomingOrders.views.from" })}</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={filters.orderDateFrom}
              onChange={(event) => handlePeriodPatch({ orderDateFrom: event.target.value })}
              error={hasInvalidDateRange}
              sx={textFieldSx}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <FieldLabel>{intl.formatMessage({ id: "incomingOrders.views.to" })}</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={filters.orderDateTo}
              onChange={(event) => handlePeriodPatch({ orderDateTo: event.target.value })}
              error={hasInvalidDateRange}
              helperText={hasInvalidDateRange ? intl.formatMessage({ id: "incomingOrders.views.dateToMustBeAfterDateFrom" }) : " "}
              sx={textFieldSx}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
};

const PlacementSelect = ({
  groups,
  groupTarget,
  onGroupTargetChange,
  onCreateGroupClick,
}) => {
  const intl = useIntl();

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0 }}>
        <InputLabel>{intl.formatMessage({ id: "incomingOrders.views.selectPlacement" })}</InputLabel>
        <Select
          value={groupTarget}
          label={intl.formatMessage({ id: "incomingOrders.views.selectPlacement" })}
          onChange={(event) => onGroupTargetChange(event.target.value)}
          sx={{
            height: 38,
            borderRadius: "8px",
            bgcolor: "background.paper",
            fontSize: 13,
          }}
        >
          <MenuItem value={ROOT_GROUP_ID}>{intl.formatMessage({ id: "incomingOrders.views.mainLevelOwnTab" })}</MenuItem>

          {groups.map((group) => (
            <MenuItem key={group.id} value={group.id}>
              {group.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Tooltip title={intl.formatMessage({ id: "incomingOrders.views.createNewGroup" })}>
        <IconButton
          aria-label={intl.formatMessage({ id: "incomingOrders.views.createNewGroup" })}
          size="small"
          onClick={onCreateGroupClick}
          sx={{
            width: 38,
            height: 38,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "divider",
            color: "text.secondary",
            bgcolor: "background.paper",
            flexShrink: 0,
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "action.hover",
              color: "primary.main",
            },
          }}
        >
          <AddRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};


const CompactViewForm = ({
  draft,
  onChange,
  groups,
  groupTarget,
  onGroupTargetChange,
  statuses,
  onCreateGroupClick,
  visibleColumns,
  onToggleColumn,
}) => {
  const intl = useIntl();
  const [showPlacementSection, setShowPlacementSection] = useState(false);
  const [filterPanelAnchor, setFilterPanelAnchor] = useState(null);
  const [columnPanelAnchor, setColumnPanelAnchor] = useState(null);

  const placementLabel = getPlacementLabel(intl, groups, groupTarget);
  const visibleColumnCount = TABLE_HEADERS.filter((columnType) => visibleColumns[columnType] !== false).length;

  const updateDraftPeriod = (nextFilters) => {


    onChange({
      ...draft,
      filters: {
        ...draft.filters,
        periodPreset: nextFilters.periodPreset,
        orderDateFrom: nextFilters.orderDateFrom ?? "",
        orderDateTo: nextFilters.orderDateTo ?? "",
      },
    });
  };

  const updateDraftFilterFields = (patch) => {

    onChange({
      ...draft,
      filters: {
        ...draft.filters,
        ...patch,
      },
    });
  };

  const handleToggleType = (typeId) => {
    const normalizedDraftFilters = draft.filters;
    const nextProductTypes = normalizedDraftFilters.productTypes.includes(typeId)
      ? normalizedDraftFilters.productTypes.filter((id) => id !== typeId)
      : [...normalizedDraftFilters.productTypes, typeId];

    updateDraftFilterFields({ productTypes: nextProductTypes });
  };

  const handleToggleStatus = (statusId) => {
    const current = draft.filters.supplierOrderStatusIds;

    const next = current.includes(statusId)
      ? current.filter((id) => id !== statusId)
      : [...current, statusId];

    updateDraftFilterFields({ supplierOrderStatusIds: next });
  };

  const handleOrderStatusChange = (orderStatus) => {
    updateDraftFilterFields({
      orderStatus,
    });
  };

  const handleClearStatuses = () => {
    updateDraftFilterFields({ supplierOrderStatusIds: [] });
  };

  const handleOpenFilterPanel = (event) => {
    setColumnPanelAnchor(null);
    setFilterPanelAnchor(event.currentTarget);
  };

  const handleOpenColumnPanel = (event) => {
    setFilterPanelAnchor(null);
    setColumnPanelAnchor(event.currentTarget);
  };



  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        label={intl.formatMessage({ id: "incomingOrders.views.name" })}
        placeholder={intl.formatMessage({ id: "incomingOrders.views.namePlaceholder" })}
        value={draft.name}
        onChange={(event) => onChange({ ...draft, name: event.target.value })}
        InputLabelProps={{ shrink: true }}
        sx={labeledTextFieldSx}
      />

      <Box>
        <SettingsIconRow
          filters={draft.filters}
          filterPanelOpen={Boolean(filterPanelAnchor)}
          onOpenFilterPanel={handleOpenFilterPanel}
          columnPanelOpen={Boolean(columnPanelAnchor)}
          onOpenColumnPanel={handleOpenColumnPanel}
          visibleColumnCount={visibleColumnCount}
        />

        <FilterSettingsPanel
          anchorEl={filterPanelAnchor}
          open={Boolean(filterPanelAnchor)}
          onClose={() => setFilterPanelAnchor(null)}
          filters={draft.filters}
          statuses={statuses}
          onToggleType={handleToggleType}
          onToggleStatus={handleToggleStatus}
          onClearStatuses={handleClearStatuses}
          onOrderStatusChange={handleOrderStatusChange}
        />

        <ColumnSettingsPanel
          anchorEl={columnPanelAnchor}
          open={Boolean(columnPanelAnchor)}
          onClose={() => setColumnPanelAnchor(null)}
          visibleColumns={visibleColumns}
          onToggleColumn={onToggleColumn}
        />
      </Box>

      <PeriodSelector filters={draft.filters} onChange={updateDraftPeriod} />

      <Box>
        <Button
          size="small"
          startIcon={
            showPlacementSection
              ? <ExpandMoreRoundedIcon fontSize="small" />
              : <ChevronRightRoundedIcon fontSize="small" />
          }
          onClick={() => setShowPlacementSection((prev) => !prev)}
          sx={{
            px: 0,
            py: 0,
            minHeight: 28,
            textTransform: "none",
            justifyContent: "flex-start",
            color: "text.secondary",
            fontSize: 12,
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          {intl.formatMessage(
            {
              id: "incomingOrders.views.placementLabel"
            },
            {
              placement: placementLabel
            }
          )}
        </Button>

        <Collapse in={showPlacementSection} unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <PlacementSelect
              groups={groups}
              groupTarget={groupTarget}
              onGroupTargetChange={onGroupTargetChange}
              onCreateGroupClick={onCreateGroupClick}
            />
          </Box>
        </Collapse>
      </Box>
    </Stack>
  );
};

export default CompactViewForm;